import { useState } from 'react';

import { faArrowsRepeat, faCopy } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

import { API_ACCOUNTS } from 'RestAPIs/endpoints';
import { useDeleteById } from 'RestAPIs/useDeleteById';

import { Box, DialogActions, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';

import TableCRUDLayout, {
  TableCRUDLayoutHandlers
} from 'components/shared/CRUD/TableCRUDLayout';
import {
  Dialog,
  FontAwesomeSvgIcon,
  showSnackbar
} from 'components/shared/index';

import { EditOrCreate } from './components/EditOrCreate';

import useIsOpen from '../../../../hooks/useIsOpen';
import { useGetApiAccounts } from '../../../../queries';
import { useGetColumnDefs } from './hooks/useGetColumnDefs';

export const ApiAccounts = () => {
  const { t } = useTranslation('common');
  const columns = useGetColumnDefs();
  const { isFetching, data } = useGetApiAccounts();
  const { open, isOpen, close } = useIsOpen();
  const [apiKey, setApiKey] = useState<string>('');
  const [isOpenForGenerating, setIsOpenForGenerating] = useState(false);

  const { mutate: handleDeleteClick } = useDeleteById(`${API_ACCOUNTS}`, [
    'apiAccounts',
    'list'
  ]);

  const onDialogClose = () => {
    close();
    setApiKey('');
  };

  const onDrawerClose = () => {
    setIsOpenForGenerating(false);
  };

  const regenerateAPIKey = (id, row, props: TableCRUDLayoutHandlers) => {
    setIsOpenForGenerating(true);
    if (props?.handleEditClick) {
      props.handleEditClick(id, row);
    }
  };

  const onClickCopy = async () => {
    if (!navigator.clipboard) {
      return;
    }
    await navigator.clipboard.writeText(apiKey);
    showSnackbar({
      severity: 'success',
      message: t('copiedToClipboard', {
        name: t('api_accounts.account_API_key')
      })
    });
  };

  const showAPIKey = (apiKey: string) => {
    setApiKey(apiKey);
    open();
  };

  return (
    <>
      <Dialog
        open={isOpen}
        handleClose={onDialogClose}
        title={t('api_accounts.account_API_key')}
      >
        <Box alignItems="center" alignContent="center">
          <Box mb={2}>
            <Typography variant="body2">
              {t('api_accounts.copy_msg_info')}
            </Typography>
          </Box>
          <Box>
            <TextField fullWidth variant="outlined" value={apiKey} multiline />
          </Box>
        </Box>

        <DialogActions className="dialog-actions">
          <Button
            onClick={onClickCopy}
            color="dark"
            variant="contained"
            startIcon={<FontAwesomeSvgIcon icon={faCopy} />}
          >
            {t('api_accounts.copy_key')}
          </Button>
        </DialogActions>
      </Dialog>
      <TableCRUDLayout
        shouldRenderToolBar={false}
        onClose={onDrawerClose}
        onDeleteClick={handleDeleteClick}
        customEditTitle={
          isOpenForGenerating ? t('api_accounts.generate_key') : undefined
        }
        FormComponent={EditOrCreate}
        additionalFormProps={{
          showAPIKey: showAPIKey,
          isOpenForGenerating: isOpenForGenerating
        }}
        columns={columns}
        loading={isFetching}
        rows={data}
        customActions={[
          {
            tooltipTitle: t('api_accounts.generate_key'),
            onClick: regenerateAPIKey,
            icon: faArrowsRepeat
          }
        ]}
        getRowId={(data) => data.accountId}
        confirmationTitle={t('deleteDialog.title')}
        confirmationBody={t('deleteDialog.body')}
      />
    </>
  );
};
